import * as React from 'react';
import {Mutation} from 'react-apollo';

import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import GoogleLogoSvg from 'shells/logos/google-g.svg';

import type {
    setGoogleTaskSyncIsEnabled as setGoogleTaskSyncIsEnabledMutation,
    setGoogleTaskSyncIsEnabledVariables as setGoogleTaskSyncIsEnabledMutationVariables,
} from 'nutshell-graphql-types';

import {authToService} from '../integrations/auth-to-service';
import {ClearIntegration} from '../integrations/clear-integration';
import {getAuthUrl} from '../integrations/integration-mutation-response-helpers';

import SET_GOOGLE_TASK_SYNC_IS_ENABLED from './graphql/mutations/set-google-task-sync-is-enabled.graphql';
import {updateCacheFromMutation} from './update-cache-from-mutation';

const googleLogo = <GoogleLogoSvg width={24} />;
const windowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 850,
} as const;

class GoogleEnableMutation extends Mutation<
    setGoogleTaskSyncIsEnabledMutation,
    setGoogleTaskSyncIsEnabledMutationVariables
> {}

type Props = {
    username: string;
    isEnabled: boolean;
    onAuthStart: () => void;
    onAuthSuccess: () => Promise<any>;
    onAuthError: (arg1: string) => void;
    externalService: 'GOOGLE' | 'GOOGLE_APPS';
    errors?: string[];
};

export function TaskWidgetGoogle(props: Props) {
    const [isAuthenticating, setIsAuthenticating] = React.useState<boolean>(false);

    return (
        <ClearIntegration>
            {({clearIntegration}) => (
                <GoogleEnableMutation
                    mutation={SET_GOOGLE_TASK_SYNC_IS_ENABLED}
                    update={updateCacheFromMutation}
                >
                    {(enableGoogle, {loading: isMutationInFlight}) => {
                        let status = props.isEnabled ? 'enabled' : 'disabled';
                        if (isMutationInFlight || isAuthenticating) {
                            status = props.isEnabled ? 'disabling' : 'enabling';
                        }

                        const hasErrors = props.errors && props.errors.length > 0;
                        const statusMessage = hasErrors ? 'Needs attention' : undefined;

                        return (
                            <IntegrationWidget
                                // @ts-expect-error
                                status={status}
                                statusMessage={statusMessage}
                                errors={props.errors}
                                account={props.username}
                                logo={googleLogo}
                                onConnectClick={() => {
                                    enableGoogle({variables: {isEnabled: true}})
                                        .then(getAuthUrl)
                                        .then((authUrl) => {
                                            // If we get an authUrl back, then we need to auth
                                            if (authUrl) {
                                                props.onAuthStart();
                                                setIsAuthenticating(true);
                                                authToService({
                                                    authUrl,
                                                    windowFeatures,
                                                    authCheckUrl: '/google/has-auth',
                                                })
                                                    .then(({hasAuth}) => {
                                                        if (hasAuth) {
                                                            props.onAuthSuccess().then(() => {
                                                                setIsAuthenticating(false);
                                                            });
                                                        }
                                                    })
                                                    .catch((errMsg) => {
                                                        props.onAuthError(errMsg);
                                                    });
                                            }
                                        });
                                }}
                                onDisconnectClick={() => {
                                    enableGoogle({variables: {isEnabled: false}});
                                }}
                                revokeWarning='This will also remove this account from email and calendar sync.'
                                onRevokeClick={() => {
                                    if (props.externalService !== 'GOOGLE_APPS') {
                                        props.onAuthStart();
                                        clearIntegration('GOOGLE').catch((errMsg) => {
                                            props.onAuthError(errMsg);
                                        });
                                    }
                                }}
                            />
                        );
                    }}
                </GoogleEnableMutation>
            )}
        </ClearIntegration>
    );
}
